import { useEffect, useRef, useState } from 'react';
import { NAV_LINKS, JOIN_US } from '../../config/data';
import { usePage, PAGES } from '../../context/PageContext';
import { Icon } from '../Icons';
import logoSrc from '../../assets/images/ai_logo.svg';
import { t } from '../../utils/i18n';
import './index.css';

const SCROLL_SOLID_THRESHOLD = 90;

export default function TopNav() {
    const { currentPage, navigate, isActive } = usePage();
    const [openDropdown, setOpenDropdown] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerExpandedKey, setDrawerExpandedKey] = useState(null);
    const closeTimer = useRef(null);

    const isHome = currentPage === PAGES.HOME;
    const [scrolledPastHero, setScrolledPastHero] = useState(!isHome);

    /* 切页时关闭已展开的下拉/抽屉，等价于原先监听 location.pathname */
    useEffect(() => {
        setOpenDropdown(null);
        setDrawerOpen(false);
        setDrawerExpandedKey(null);
    }, [currentPage]);

    /* 仅在首页时根据滚动切换 transparent/solid；其他页固定 solid */
    useEffect(() => {
        if (!isHome) {
            setScrolledPastHero(true);
            return undefined;
        }
        const handleScroll = () => {
            setScrolledPastHero(window.scrollY > SCROLL_SOLID_THRESHOLD);
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isHome]);

    /* 抽屉打开时锁滚动 */
    useEffect(() => {
        if (drawerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [drawerOpen]);

    const handleEnter = (idx) => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
        setOpenDropdown(idx);
    };

    const handleLeave = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpenDropdown(null), 120);
    };

    const go = (target) => navigate(target);

    const variantClass = isHome && !scrolledPastHero
        ? 'ubiquantai-topnav--transparent'
        : 'ubiquantai-topnav--solid';

    return (
        <>
            <nav className={`ubiquantai-topnav ${variantClass}`}>
                <div className="ubiquantai-topnav-inner">
                    <button
                        type="button"
                        className="ubiquantai-logo"
                        aria-label={t('至知创新研究院', 'IQuest Research')}
                        onClick={() => go(PAGES.HOME)}
                    >
                        <span className="ubiquantai-logo-dot" aria-hidden />
                        <img src={logoSrc} alt="至知" className="ubiquantai-logo-img" />
                    </button>

                    <ul className="ubiquantai-topnav-list">
                        {NAV_LINKS.map((item, idx) => {
                            const hasChildren = !!item.children?.length;
                            const active = isActive(item.path);
                            return (
                                <li
                                    key={item.name}
                                    className={[
                                        'ubiquantai-topnav-item',
                                        hasChildren ? 'has-dropdown' : '',
                                        active ? 'current' : '',
                                    ].filter(Boolean).join(' ')}
                                    onMouseEnter={() => hasChildren && handleEnter(idx)}
                                    onMouseLeave={() => hasChildren && handleLeave()}
                                >
                                    <button
                                        type="button"
                                        className={`ubiquantai-topnav-label${active ? ' is-active' : ''}`}
                                        onClick={() => go(item.path)}
                                    >
                                        <span>{item.name}</span>
                                        {hasChildren && (
                                            <svg
                                                className="topnav-arrow"
                                                viewBox="0 0 10 6"
                                                fill="none"
                                            >
                                                <path
                                                    d="M1 1L5 5L9 1"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        )}
                                    </button>

                                    {hasChildren && (
                                        <div
                                            className={`ubiquantai-topnav-dropdown${openDropdown === idx ? ' open' : ''}`}
                                        >
                                            <ul>
                                                {item.children.map((child) => {
                                                    const childActive = isActive(child.path);
                                                    return (
                                                        <li key={child.path}>
                                                            <button
                                                                type="button"
                                                                className={`ubiquantai-topnav-dropdown-item${childActive ? ' current' : ''}`}
                                                                onClick={() => go(child.path)}
                                                            >
                                                                {child.name}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    <div className="ubiquantai-topnav-actions">
                        <a
                            className="btn btn-outline-strong ubiquantai-topnav-cta"
                            href={JOIN_US.href || 'mailto:hr@iquestlab.com'}
                            target={JOIN_US.href ? (JOIN_US.target || '_blank') : undefined}
                            rel={JOIN_US.href ? 'noreferrer' : undefined}
                        >
                            <span>{t('加入我们', 'Join Us')}</span>
                            <svg className="btn-arrow" viewBox="0 0 16 16" fill="none" aria-hidden>
                                <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </a>
                        <button
                            type="button"
                            className="ubiquantai-topnav-hamburger"
                            aria-label={drawerOpen ? t('关闭菜单', 'Close menu') : t('打开菜单', 'Open menu')}
                            aria-expanded={drawerOpen}
                            onClick={() => setDrawerOpen((v) => !v)}
                        >
                            <Icon name={drawerOpen ? 'x' : 'menu'} width={22} height={22} />
                        </button>
                    </div>
                </div>
            </nav>

            {/* 移动端抽屉 (额外-B) */}
            <div
                className={`ubiquantai-mobile-drawer${drawerOpen ? ' is-open' : ''}`}
                aria-hidden={!drawerOpen}
            >
                <div
                    className="ubiquantai-mobile-drawer-backdrop"
                    onClick={() => setDrawerOpen(false)}
                />
                <div className="ubiquantai-mobile-drawer-panel" role="dialog" aria-modal="true">
                    <div className="ubiquantai-mobile-drawer-header">
                        <button
                            type="button"
                            className="ubiquantai-logo"
                            onClick={() => {
                                setDrawerOpen(false);
                                go(PAGES.HOME);
                            }}
                        >
                            <img src={logoSrc} alt="至知" className="ubiquantai-logo-img" />
                        </button>
                        <button
                            type="button"
                            className="ubiquantai-mobile-drawer-close"
                            aria-label={t('关闭菜单', 'Close menu')}
                            onClick={() => setDrawerOpen(false)}
                        >
                            <Icon name="x" width={22} height={22} />
                        </button>
                    </div>
                    <ul className="ubiquantai-mobile-drawer-list">
                        {NAV_LINKS.map((item) => {
                            const hasChildren = !!item.children?.length;
                            const expanded = drawerExpandedKey === item.name;
                            return (
                                <li key={item.name} className="ubiquantai-mobile-drawer-item">
                                    {hasChildren ? (
                                        <>
                                            <button
                                                type="button"
                                                className={`ubiquantai-mobile-drawer-link is-toggle${expanded ? ' is-open' : ''}`}
                                                onClick={() => setDrawerExpandedKey(expanded ? null : item.name)}
                                            >
                                                <span>{item.name}</span>
                                                <svg viewBox="0 0 10 6" fill="none" width="10" height="6">
                                                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                            {expanded && (
                                                <ul className="ubiquantai-mobile-drawer-sublist">
                                                    {item.children.map((child) => (
                                                        <li key={child.path}>
                                                            <button
                                                                type="button"
                                                                className="ubiquantai-mobile-drawer-sublink"
                                                                onClick={() => {
                                                                    setDrawerOpen(false);
                                                                    go(child.path);
                                                                }}
                                                            >
                                                                {child.name}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            className="ubiquantai-mobile-drawer-link"
                                            onClick={() => {
                                                setDrawerOpen(false);
                                                go(item.path);
                                            }}
                                        >
                                            {item.name}
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    <div className="ubiquantai-mobile-drawer-footer">
                        <a
                            className="btn btn-primary btn-large"
                            href="mailto:hr@iquestlab.com"
                            onClick={() => setDrawerOpen(false)}
                        >
                            {t('联系我们', 'Contact Us')}
                        </a>
                    </div>
                </div>
            </div>
        </>
    );
}
